import { Alert, Badge, Button, Card, Field, Input, Modal, Progress, Textarea } from "@aman/design-system";
import { type ChangeEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api, ApiError, asApiError, type ApiError as ApiErrorType } from "../api/client";
import { useApiQuery } from "../api/hooks";
import { ApiErrorAlert, DataTable, LoadingTable, PageHeader, SearchBar, StatusBadge, useFilteredRows } from "../components/common";
import { isRecord, listFromPayload, objectFromPayload, stringValue, type AnyRecord } from "../types";

const idOf = (record: AnyRecord): string => stringValue(record["pluginId"], stringValue(record["plugin_id"], stringValue(record["id"])));

function csrfToken(): string | undefined {
  const cookie = document.cookie.split("; ").find((item) => item.startsWith("XSRF-TOKEN="));
  return cookie === undefined ? undefined : decodeURIComponent(cookie.slice(11));
}

function uploadPluginBundle(path: "/plugins/inspect" | "/plugins/import", file: File,
  fields: Record<string, string>, idempotencyKey: string, onProgress: (value: number) => void,
  onRequest: (request: XMLHttpRequest) => void): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const base = (import.meta.env.VITE_API_BASE_URL ?? "/api/v1").replace(/\/$/u, "");
    const xhr = new XMLHttpRequest();
    onRequest(xhr);
    xhr.open("POST", `${base}${path}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader("Accept", "application/json");
    xhr.setRequestHeader("Idempotency-Key", idempotencyKey);
    const csrf = csrfToken();
    if (csrf !== undefined) xhr.setRequestHeader("X-CSRF-Token", csrf);
    xhr.upload.onprogress = (event) => { if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 90)); };
    xhr.onerror = () => reject(new ApiError({ code: "SERVICE_UNAVAILABLE", message: "Plugin upload failed", status: 0 }));
    xhr.onabort = () => reject(new ApiError({ code: "SERVICE_UNAVAILABLE", message: "Plugin operation was cancelled", status: 0 }));
    xhr.onload = () => {
      let parsed: unknown;
      try { parsed = xhr.responseText === "" ? null : JSON.parse(xhr.responseText); }
      catch { parsed = xhr.responseText; }
      if (xhr.status < 200 || xhr.status >= 300 || (isRecord(parsed) && parsed["ok"] === false)) {
        const failure = isRecord(parsed) && isRecord(parsed["error"]) ? parsed["error"] : {};
        reject(new ApiError({
          code: stringValue(failure["code"], xhr.status >= 500 ? "SERVICE_UNAVAILABLE" : "PLUGIN_BUNDLE_INVALID"),
          message: stringValue(failure["message"], xhr.statusText), status: xhr.status,
          ...(failure["details"] !== undefined ? { details: failure["details"] } : {})
        }));
        return;
      }
      onProgress(100);
      resolve(isRecord(parsed) && parsed["ok"] === true ? parsed["data"] : parsed);
    };
    const form = new FormData();
    Object.entries(fields).forEach(([name, value]) => form.set(name, value));
    form.set("bundle", file, file.name);
    xhr.send(form);
  });
}

async function waitForBackup(id: string, cancelled: () => boolean, onStatus: (status: string) => void): Promise<void> {
  for (let attempt = 0; attempt < 450; attempt += 1) {
    if (cancelled()) throw new Error("PLUGIN_OPERATION_CANCELLED");
    let backup: AnyRecord;
    try { backup = objectFromPayload(await api.get<unknown>(`/backups/${encodeURIComponent(id)}`)); }
    catch (error) {
      if (error instanceof ApiError && error.status === 503) {
        onStatus("running"); await new Promise((resolve) => window.setTimeout(resolve, 2_000)); continue;
      }
      throw error;
    }
    const status = stringValue(backup["status"]); onStatus(status);
    if (status === "completed") return;
    if (status === "failed" || status === "pruned") {
      throw new ApiError({ code: "BACKUP_FAILED", message: `Automatic backup ${status}: ${stringValue(backup["errorCode"], "unknown")}`, status: 409, details: backup });
    }
    await new Promise((resolve) => window.setTimeout(resolve, 2_000));
  }
  throw new ApiError({ code: "BACKUP_NOT_READY", message: "Timed out while waiting for the automatic backup", status: 408 });
}

export function PluginsPage() {
  const { t } = useTranslation();
  const query = useApiQuery<unknown>("/plugins");
  const [search, setSearch] = useState("");
  const [installOpen, setInstallOpen] = useState(new URLSearchParams(location.search).has("install"));
  const [file, setFile] = useState<File>();
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState<string>();
  const [inspection, setInspection] = useState<AnyRecord>();
  const [warningReason, setWarningReason] = useState("");
  const [archiveReason, setArchiveReason] = useState("");
  const [rollbackReason, setRollbackReason] = useState("");
  const [rollbackConfirmation, setRollbackConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiErrorType>();
  const [success, setSuccess] = useState(false);
  const [detail, setDetail] = useState<AnyRecord>();
  const [versions, setVersions] = useState<AnyRecord[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [logs, setLogs] = useState<string>();
  const input = useRef<HTMLInputElement>(null);
  const activeRequest = useRef<XMLHttpRequest>();
  const cancelled = useRef(false);
  const rows = useFilteredRows(listFromPayload(query.data), search);

  const mutate = async (work: () => Promise<unknown>, close = false): Promise<boolean> => {
    setBusy(true); setError(undefined); setSuccess(false);
    try {
      await work(); setSuccess(true);
      if (close) setInstallOpen(false);
      await query.reload();
      return true;
    } catch (caught) {
      setError(asApiError(caught));
      return false;
    } finally { setBusy(false); }
  };

  const lifecycle = async (record: AnyRecord, action: "enable" | "disable" | "health"): Promise<void> => {
    await mutate(() => api.post(`/plugins/${encodeURIComponent(idOf(record))}/${action}`, {}));
  };
  const inspect = async (): Promise<void> => {
    if (file === undefined) {
      setError(new ApiError({ code: "PLUGIN_BUNDLE_INVALID", message: t("pages.plugins.noFile"), status: 400 }));
      return;
    }
    cancelled.current = false; setBusy(true); setError(undefined); setInspection(undefined); setProgress(1);
    setPhase(t("pages.plugins.inspecting"));
    try {
      const result = await uploadPluginBundle("/plugins/inspect", file, {}, crypto.randomUUID(), setProgress,
        (request) => { activeRequest.current = request; });
      setInspection(objectFromPayload(result)); setProgress(100); setPhase(undefined);
    } catch (caught) { if (!cancelled.current) setError(asApiError(caught)); }
    finally { activeRequest.current = undefined; setBusy(false); }
  };
  const install = async (): Promise<void> => {
    if (file === undefined || inspection === undefined) return;
    const report = objectFromPayload(inspection["report"]);
    const warnings = Array.isArray(report["warnings"]) ? report["warnings"] : [];
    if (report["valid"] !== true) return;
    if (warnings.length > 0 && warningReason.trim().length < 10) {
      setError(new ApiError({ code: "PLUGIN_WARNING_ACK_REQUIRED", message: t("pages.plugins.warningReasonRequired"), status: 409 }));
      return;
    }
    cancelled.current = false; setBusy(true); setError(undefined); setSuccess(false); setProgress(1);
    setPhase(t("pages.plugins.preparingBackup"));
    const idempotencyKey = crypto.randomUUID();
    const fields = warningReason.trim() === "" ? {} : { warningOverrideReason: warningReason.trim() };
    try {
      let result = objectFromPayload(await uploadPluginBundle("/plugins/import", file, fields, idempotencyKey,
        (value) => setProgress(Math.max(1, Math.round(value * 0.5))), (request) => { activeRequest.current = request; }));
      if (result["installationReady"] === false) {
        const backup = objectFromPayload(result["backup"]); const backupId = stringValue(backup["id"]);
        setProgress(48); setPhase(t("pages.plugins.backupPreparing", { id: backupId }));
        await waitForBackup(backupId, () => cancelled.current,
          (status) => setPhase(t("pages.plugins.backupStatus", { status })));
        if (cancelled.current) return;
        setPhase(t("pages.plugins.installing"));
        result = objectFromPayload(await uploadPluginBundle("/plugins/import", file, fields, idempotencyKey,
          (value) => setProgress(50 + Math.round(value * 0.5)), (request) => { activeRequest.current = request; }));
      }
      if (result["installationReady"] !== true) {
        throw new ApiError({ code: "BACKUP_NOT_READY", message: "Installation did not become ready", status: 409 });
      }
      setProgress(100); setSuccess(true); setInstallOpen(false); setInspection(undefined); setFile(undefined);
      await query.reload();
    } catch (caught) { if (!cancelled.current) setError(asApiError(caught)); }
    finally { activeRequest.current = undefined; setBusy(false); setPhase(undefined); }
  };
  const cancelOperation = (): void => {
    cancelled.current = true; activeRequest.current?.abort(); activeRequest.current = undefined;
    setBusy(false); setProgress(0); setPhase(undefined);
  };
  const loadVersions = async (record: AnyRecord): Promise<void> => {
    setVersionsLoading(true);
    try {
      const result = await api.get<unknown>(`/plugins/${encodeURIComponent(idOf(record))}/versions`);
      setVersions(listFromPayload(result));
    } catch (caught) { setError(asApiError(caught)); }
    finally { setVersionsLoading(false); }
  };
  const openDetail = async (record: AnyRecord): Promise<void> => {
    setDetail(record); setLogs(undefined); setVersions([]); setError(undefined); setArchiveReason("");
    setRollbackReason(""); setRollbackConfirmation("");
    await loadVersions(record);
  };
  const retryPending = async (): Promise<void> => {
    if (detail === undefined) return;
    const changed = await mutate(() => api.post(`/plugins/${encodeURIComponent(idOf(detail))}/enable`, {}));
    if (changed) { await loadVersions(detail); setDetail(undefined); }
  };
  const cancelPending = async (): Promise<void> => {
    if (detail === undefined || !window.confirm(t("pages.plugins.cancelPendingConfirm"))) return;
    const changed = await mutate(() => api.post(`/plugins/${encodeURIComponent(idOf(detail))}/pending/cancel`, {}));
    if (changed) { await loadVersions(detail); setDetail(undefined); }
  };
  const openLogs = async (record: AnyRecord): Promise<void> => {
    setBusy(true); setError(undefined);
    try {
      const result = await api.get<unknown>(`/plugins/${encodeURIComponent(idOf(record))}/logs?tail=200`);
      const data = objectFromPayload(result); setDetail(record);
      setLogs(stringValue(data["logs"], Array.isArray(result) ? result.join("\n") : typeof result === "string" ? result : JSON.stringify(result, null, 2)));
    } catch (caught) { setError(asApiError(caught)); }
    finally { setBusy(false); }
  };
  const fileChanged = (event: ChangeEvent<HTMLInputElement>): void => {
    setFile(event.target.files?.[0]); setProgress(0); setError(undefined); setInspection(undefined); setWarningReason("");
  };
  const archivePlugin = async (): Promise<void> => {
    if (detail === undefined || archiveReason.trim().length < 10 || !window.confirm(t("pages.plugins.archiveConfirm"))) return;
    const changed = await mutate(() => api.post(`/plugins/${encodeURIComponent(idOf(detail))}/archive`, { reason: archiveReason.trim() }));
    if (changed) { setDetail(undefined); setArchiveReason(""); }
  };
  const rollbackPlugin = async (): Promise<void> => {
    if (detail === undefined || rollbackReason.trim().length < 10) return;
    const changed = await mutate(() => api.post(`/plugins/${encodeURIComponent(idOf(detail))}/rollback`, {
      reason: rollbackReason.trim(), confirmation: rollbackConfirmation.trim(),
    }));
    if (changed) { setDetail(undefined); setRollbackReason(""); setRollbackConfirmation(""); }
  };
  const pendingVersion = stringValue(detail?.["pendingVersion"], stringValue(detail?.["pending_version"]));
  const pendingRecord = versions.find((version) => version["pending"] === true);
  const migrationStatus = stringValue(pendingRecord?.["migrationStatus"], stringValue(pendingRecord?.["migration_status"]));
  const canActivatePending = pendingVersion !== "" && ["succeeded", "not_required"].includes(migrationStatus);
  const report = objectFromPayload(inspection?.["report"]);
  const reportErrors = Array.isArray(report["errors"]) ? report["errors"].filter(isRecord) : [];
  const reportWarnings = Array.isArray(report["warnings"]) ? report["warnings"].filter(isRecord) : [];
  const currentVersionRecord = versions.find((version) => version["current"] === true);
  const rollbackPhrase = `ROLLBACK ${idOf(detail ?? {})}@${stringValue(currentVersionRecord?.["version"])}`;
  const canRollback = stringValue(currentVersionRecord?.["installBackupStatus"]) === "completed";

  return <div className="page-stack">
    <PageHeader title={t("pages.plugins.title")} subtitle={t("pages.plugins.subtitle")} actions={<>
      <Link className="aman-button aman-button--secondary" to="/plugins/builder">✣ {t("nav.pluginBuilder")}</Link>
      <Button type="button" onClick={() => setInstallOpen(true)}>↑ {t("pages.plugins.uploadInstall")}</Button>
    </>} />
    <ApiErrorAlert error={query.error} onRetry={() => void query.reload()} />
    <ApiErrorAlert error={error} />
    {success ? <Alert tone="success">{t("common.success")}</Alert> : null}
    <Card bodyClassName="card-body--flush">
      <SearchBar value={search} onChange={setSearch} onRefresh={() => void query.reload()} loading={query.loading} />
      {query.loading && query.data === undefined ? <LoadingTable columns={7} /> : <DataTable rows={rows} columns={[
        { key: "name", label: t("common.name"), render: (row) => <div className="plugin-name"><span className="plugin-logo">{stringValue(row["name"], idOf(row)).slice(0, 1).toLocaleUpperCase()}</span><div><strong>{stringValue(row["name"], idOf(row))}</strong><code>{idOf(row)}</code></div></div> },
        { key: "version", label: t("common.version"), render: (row) => <div><strong>{stringValue(row["currentVersion"], stringValue(row["current_version"]))}</strong>{stringValue(row["pendingVersion"], stringValue(row["pending_version"])) !== "" ? <div><Badge tone="warning">{t("common.pending")}: {stringValue(row["pendingVersion"], stringValue(row["pending_version"]))}</Badge></div> : null}</div> },
        { key: "status", label: t("common.status"), render: (row) => <StatusBadge value={row["status"]} /> },
        { key: "healthStatus", label: t("pages.plugins.health"), render: (row) => <StatusBadge value={row["healthStatus"] ?? row["health_status"]} /> },
        { key: "lastHealthAt", label: t("common.date"), render: (row) => stringValue(row["lastHealthAt"], stringValue(row["last_health_at"], "—")) }
      ]} actions={(row) => <>
        <Button type="button" size="small" variant="secondary" busy={busy} onClick={() => void lifecycle(row, stringValue(row["status"]) === "enabled" ? "disable" : "enable")}>{stringValue(row["status"]) === "enabled" ? t("pages.plugins.disable") : t("pages.plugins.enable")}</Button>
        <Button type="button" size="small" variant="ghost" busy={busy} onClick={() => void lifecycle(row, "health")}>{t("pages.plugins.health")}</Button>
        <Link className="aman-button aman-button--secondary aman-button--small" to={`/plugins/${encodeURIComponent(idOf(row))}/ui`}>{t("pages.plugins.openUi")}</Link>
        <Button type="button" size="small" variant="ghost" onClick={() => void openDetail(row)}>{t("common.details")}</Button>
        <Button type="button" size="small" variant="ghost" busy={busy} onClick={() => void openLogs(row)}>{t("pages.plugins.logs")}</Button>
      </>} />}
    </Card>

    <Modal open={installOpen} title={t("pages.plugins.uploadInstall")} onClose={() => { if (busy) cancelOperation(); setInstallOpen(false); }}>
      <ApiErrorAlert error={error} />
      <div className="upload-dropzone" role="button" tabIndex={0} onClick={() => input.current?.click()} onKeyDown={(event) => { if (event.key === "Enter") input.current?.click(); }}>
        <span>⇧</span><strong>{file?.name ?? t("pages.plugins.uploadInstall")}</strong><small>.tar / .tar.gz · max 2 GB</small>
        <input ref={input} className="aman-visually-hidden" type="file" accept=".tar,.gz,.tgz,application/x-tar,application/gzip" onChange={fileChanged} />
      </div>
      {file !== undefined ? <div className="selected-file"><span>{t("pages.plugins.selectedFile")}</span><strong dir="ltr">{file.name}</strong><small>{(file.size / 1024 / 1024).toFixed(2)} MB</small></div> : null}
      {progress > 0 ? <Progress value={progress} label={`${t("pages.plugins.installProgress")} — ${progress}%`} /> : null}
      {phase !== undefined ? <Alert tone="info">{phase}</Alert> : null}
      {inspection !== undefined ? <Alert tone={report["valid"] === true ? "success" : "error"}
        title={report["valid"] === true ? t("pages.plugins.inspectionPassed") : t("pages.plugins.inspectionFailed")}>
        <div><strong>{t("pages.plugins.errors")}:</strong> {reportErrors.length}</div>
        <div><strong>{t("pages.plugins.warnings")}:</strong> {reportWarnings.length}</div>
        {[...reportErrors, ...reportWarnings].map((issue, index) => <div key={`${stringValue(issue["code"])}-${index}`}>
          <code>{stringValue(issue["code"])}</code> {stringValue(issue["message"])}</div>)}
      </Alert> : null}
      {reportWarnings.length > 0 ? <Field label={t("pages.plugins.warningReason")} hint={t("pages.plugins.warningReasonHint")}>
        <Textarea required minLength={10} maxLength={1000} value={warningReason} onChange={(event) => setWarningReason(event.target.value)} />
      </Field> : null}
      <div className="modal-inline-actions">
        {inspection === undefined
          ? <Button type="button" busy={busy} disabled={file === undefined} onClick={() => void inspect()}>{t("pages.plugins.inspectBundle")}</Button>
          : <Button type="button" busy={busy} disabled={report["valid"] !== true} onClick={() => void install()}>{t("pages.plugins.installApproved")}</Button>}
        <Button type="button" variant="secondary" onClick={() => { if (busy) cancelOperation(); setInstallOpen(false); }}>{t("common.cancel")}</Button>
      </div>
    </Modal>

    <Modal open={detail !== undefined && logs === undefined} title={stringValue(detail?.["name"], idOf(detail ?? {}))} onClose={() => setDetail(undefined)}>
      <div className="scope-sections"><ScopeList title={t("pages.plugins.requestedScopes")} scopes={detail?.["requestedScopes"] ?? detail?.["requested_scopes"]} /><ScopeList title={t("pages.plugins.grantedScopes")} scopes={detail?.["grantedScopes"] ?? detail?.["granted_scopes"]} /></div>
      <dl className="key-values">
        <div><dt>ID</dt><dd><code>{idOf(detail ?? {})}</code></dd></div>
        <div><dt>{t("pages.plugins.currentVersion")}</dt><dd>{stringValue(detail?.["currentVersion"], stringValue(detail?.["current_version"]))}</dd></div>
        <div><dt>{t("pages.plugins.pendingVersion")}</dt><dd>{pendingVersion || "—"}</dd></div>
        <div><dt>{t("common.status")}</dt><dd><StatusBadge value={detail?.["status"]} /></dd></div>
      </dl>
      <h3>{t("pages.plugins.versionHistory")}</h3>
      {versionsLoading ? <LoadingTable rows={3} columns={6} /> : <DataTable rows={versions} rowKey={(row) => stringValue(row["version"])} columns={[
        { key: "version", label: t("common.version") },
        { key: "status", label: t("common.status"), render: (row) => <StatusBadge value={row["status"]} /> },
        { key: "migrationStatus", label: t("pages.plugins.migrations"), render: (row) => <StatusBadge value={row["migrationStatus"]} /> },
        { key: "installedAt", label: t("common.date") },
        { key: "installBackupStatus", label: t("pages.plugins.safetyBackup"), render: (row) => stringValue(row["installBackupId"]) === "" ? "—" : <div><StatusBadge value={row["installBackupStatus"]} /><code>{stringValue(row["installBackupId"]).slice(0, 8)}…</code></div> },
        { key: "failureDetail", label: t("common.reason"), render: (row) => stringValue(row["failureDetail"], "—") }
      ]} />}
      {pendingVersion !== "" ? <div className="modal-inline-actions">
        {canActivatePending ? <Button type="button" busy={busy} onClick={() => void retryPending()}>{stringValue(pendingRecord?.["status"]) === "failed" ? t("common.retry") : t("pages.plugins.activatePending")}</Button> : null}
        <Button type="button" variant="secondary" busy={busy} onClick={() => void cancelPending()}>{t("pages.plugins.cancelPending")}</Button>
      </div> : null}
      {stringValue(detail?.["status"]) !== "archived" ? <div className="form-grid">
        <Field label={t("pages.plugins.archiveReason")}><Textarea minLength={10} maxLength={1000} value={archiveReason} onChange={(event) => setArchiveReason(event.target.value)} /></Field>
        <Button type="button" variant="danger" busy={busy} disabled={archiveReason.trim().length < 10} onClick={() => void archivePlugin()}>{t("pages.plugins.archive")}</Button>
      </div> : <Alert tone="warning">{t("pages.plugins.archivedPreserved")}</Alert>}
      {canRollback ? <div className="form-grid">
        <Alert tone="warning">{t("pages.plugins.rollbackWarning")}</Alert>
        <Field label={t("pages.plugins.rollbackReason")}><Textarea minLength={10} maxLength={1000} value={rollbackReason} onChange={(event) => setRollbackReason(event.target.value)} /></Field>
        <Field label={t("pages.plugins.rollbackConfirmation")} hint={t("pages.plugins.rollbackConfirmationHint", { value: rollbackPhrase })}>
          <Input dir="ltr" value={rollbackConfirmation} placeholder={rollbackPhrase} onChange={(event) => setRollbackConfirmation(event.target.value)} />
        </Field>
        <Button type="button" variant="danger" busy={busy} disabled={rollbackReason.trim().length < 10 || rollbackConfirmation.trim() !== rollbackPhrase} onClick={() => void rollbackPlugin()}>{t("pages.plugins.rollback")}</Button>
      </div> : null}
    </Modal>
    <Modal open={logs !== undefined} title={t("pages.plugins.logs")} onClose={() => { setLogs(undefined); setDetail(undefined); }}><pre className="log-viewer" dir="ltr">{logs}</pre></Modal>
  </div>;
}

function ScopeList({ title, scopes }: { title: string; scopes: unknown }) {
  const values = Array.isArray(scopes) ? scopes.filter((scope): scope is string => typeof scope === "string") : [];
  return <section><h3>{title}</h3><div className="scope-list">{values.length === 0 ? <span>—</span> : values.map((scope) => <Badge tone="info" key={scope}>{scope}</Badge>)}</div></section>;
}
